import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, DollarSign, Calendar, Filter, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { DonationTypesAPI, DonationTypeWithAmounts, TimePeriod } from '../../lib/sdk/index';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { formatDate, safeParseNumber } from '../../lib/utils/formatting';
import { getErrorMessage } from '../../lib/utils/errorMessage';

const DonationTypes: React.FC = () => {
  const [donationTypes, setDonationTypes] = useState<DonationTypeWithAmounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('month');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [currentPeriod, setCurrentPeriod] = useState<{
    type: string;
    startDate: string;
    endDate: string;
  } | null>(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingType, setEditingType] = useState<DonationTypeWithAmounts | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingType, setDeletingType] = useState<DonationTypeWithAmounts | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Form errors
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    loadDonationTypes();
  }, [timePeriod]);

  const loadDonationTypes = async () => {
    try {
      setLoading(true);
      
      // Try local data first (offline-first)
      let typesData: DonationTypeWithAmounts[] = [];
      let periodData: { type: string; startDate: string; endDate: string } | null = null;
      
      try {
        if (window.electronAPI?.dbGetAllDonationTypes && window.electronAPI?.dbGetAllDonations) {
          const [localTypes, localDonations] = await Promise.all([
            window.electronAPI.dbGetAllDonationTypes(),
            window.electronAPI.dbGetAllDonations(10000, 0)
          ]);
          
          if (localTypes.success && localTypes.data && localDonations.success && localDonations.data) {
            const types = localTypes.data;
            const donations = localDonations.data as any[];
            
            // Calculate period
            const now = new Date();
            let startDate = new Date(0); // All time
            if (timePeriod === 'week') {
              startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            } else if (timePeriod === 'month') {
              startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            } else if (timePeriod === 'year') {
              startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            }
            
            periodData = {
              type: timePeriod,
              startDate: startDate.toISOString(),
              endDate: now.toISOString()
            };
            
            // Filter donations by period
            const periodDonations = donations.filter((d: any) => {
              const date = new Date(d.created_at || d.createdAt);
              return date >= startDate;
            });
            
            // Calculate amounts per type
            typesData = types.map((type: any) => {
              const typeDonations = periodDonations.filter((d: any) => {
                const typeId = typeof d.donationTypeId === 'string' ? d.donationTypeId : d.donationTypeId?._id;
                return typeId === type._id || typeId === type._id?.toString();
              });
              
              const amount = typeDonations.reduce((sum: number, d: any) => {
                const donationAmount = safeParseNumber(d.amount, 0);
                return sum + donationAmount;
              }, 0);
              
              return {
                ...type,
                amount,
                periodAmount: amount, // Also set periodAmount for display
                count: typeDonations.length,
                periodCount: typeDonations.length
              };
            });
          }
        }
      } catch (error) {
        console.warn('Error loading from local DB, falling back to API:', error);
      }
      
      // If online, try API (stale-while-revalidate)
      if (navigator.onLine && typesData.length === 0) {
        try {
          const response = await DonationTypesAPI.getDonationTypesWithAmounts(timePeriod);
          
          if (response && response.success && response.data && Array.isArray(response.data)) {
            typesData = response.data;
            periodData = response.period;
          } else if (Array.isArray(response)) {
            typesData = response;
          }
        } catch (error: any) {
          console.warn('API call failed, using local data:', error);
        }
      }
      
      setDonationTypes(typesData);
      setCurrentPeriod(periodData);
      
      if (typesData.length === 0) {
        toast.warning('No donation types available. Please ensure you are online or have synced data locally.');
      }
    } catch (error: any) {
      console.error('Failed to load donation types:', error);
      toast.error('Failed to load donation types');
      setDonationTypes([]);
      setCurrentPeriod(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors: {[key: string]: string} = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (editingType) {
        await DonationTypesAPI.updateDonationType(editingType._id, formData);
        toast.success('Donation type updated successfully');
      } else {
        await DonationTypesAPI.createDonationType(formData);
        toast.success('Donation type created successfully');
      }
      
      closeModal();
      loadDonationTypes();
    } catch (error: any) {
      console.error('Failed to save donation type:', error);
      
      // Better error messages based on HTTP status codes
      let errorMessage = 'Failed to save donation type';
      
      // Check if it's an Axios error with response status
      if (error.response && error.response.status) {
        const status = error.response.status;
        switch (status) {
          case 400:
            errorMessage = 'Invalid data provided. Please check your input.';
            break;
          case 409:
            errorMessage = 'A donation type with this name already exists. Please choose a different name.';
            break;
          case 404:
            errorMessage = 'Donation type not found. It may have been deleted.';
            break;
          case 500:
            errorMessage = 'Server error occurred. Please try again later.';
            break;
          default:
            errorMessage = getErrorMessage(error, errorMessage);
        }
      } else if (error.message) {
        // Check if error message contains status code
        if (error.message.includes('409')) {
          errorMessage = 'A donation type with this name already exists. Please choose a different name.';
        } else if (error.message.includes('400')) {
          errorMessage = 'Invalid data provided. Please check your input.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Donation type not found. It may have been deleted.';
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error occurred. Please try again later.';
        } else {
          errorMessage = getErrorMessage(error, errorMessage);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleEdit = (type: DonationTypeWithAmounts) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || ''
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const handleDeleteClick = (type: DonationTypeWithAmounts) => {
    setDeletingType(type);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingType) return;
    
    try {
      await DonationTypesAPI.deleteDonationType(deletingType._id);
      toast.success('Donation type deleted successfully');
      setShowDeleteConfirm(false);
      setDeletingType(null);
      loadDonationTypes();
    } catch (error: any) {
      console.error('Failed to delete donation type:', error);
      
      let errorMessage = 'Failed to delete donation type';
      
      // Check if it's an Axios error with response status
      if (error.response && error.response.status) {
        const status = error.response.status;
        switch (status) {
          case 404:
            errorMessage = 'Donation type not found. It may have been deleted already.';
            break;
          case 500:
            errorMessage = 'Server error occurred. Please try again later.';
            break;
          default:
            errorMessage = getErrorMessage(error, errorMessage);
        }
      } else if (error.message) {
        if (error.message.includes('404')) {
          errorMessage = 'Donation type not found. It may have been deleted already.';
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error occurred. Please try again later.';
        } else {
          errorMessage = getErrorMessage(error, errorMessage);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleToggleStatus = async (type: DonationTypeWithAmounts) => {
    try {
      await DonationTypesAPI.updateDonationType(type._id, {
        isActive: !type.isActive
      });
      toast.success(`Donation type ${type.isActive ? 'deactivated' : 'activated'} successfully`);
      loadDonationTypes();
    } catch (error: any) {
      console.error('Failed to toggle donation type status:', error);
      
      let errorMessage = 'Failed to update donation type status';
      if (error.response && error.response.status) {
        const status = error.response.status;
        switch (status) {
          case 404:
            errorMessage = 'Donation type not found. It may have been deleted.';
            break;
          case 500:
            errorMessage = 'Server error occurred. Please try again later.';
            break;
          default:
            errorMessage = getErrorMessage(error, errorMessage);
        }
      } else if (error.message) {
        errorMessage = getErrorMessage(error, errorMessage);
      }
      
      toast.error(errorMessage);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingType(null);
    setFormData({ name: '', description: '' });
    setFormErrors({});
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeletingType(null);
  };

  const filteredTypes = donationTypes.filter(type => {
    const matchesSearch = type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (type.description && type.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && type.isActive) ||
      (statusFilter === 'inactive' && !type.isActive);
    
    return matchesSearch && matchesStatus;
  });

  const formatAmount = (amount: number | undefined | null) => {
    const safeAmount = safeParseNumber(amount, 0);
    if (safeAmount === 0) return 'Rs. 0';
    return `Rs. ${safeAmount.toLocaleString('en-PK')}`;
  };

  const getPeriodLabel = (period: TimePeriod) => {
    switch (period) {
      case 'month': return 'Month';
      case 'biAnnual': return 'Bi-Annual';
      case 'annual': return 'Annual';
      default: return 'Month';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Donation Types</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage different categories of donations</p>
        </div>
        <Button
          variant="primary"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setShowAddModal(true)}
        >
          Add Donation Type
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <DollarSign className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Types</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{donationTypes.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <Filter className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Types</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {donationTypes.filter(type => type.isActive).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <Filter className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Inactive Types</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {donationTypes.filter(type => !type.isActive).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <Calendar className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Current Period</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {getPeriodLabel(timePeriod)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search donation types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Status:
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="form-select"
            >
              <option value="all">All Types</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Time Period:
            </label>
            <select
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
              className="form-select"
            >
              <option value="month">Month</option>
              <option value="biAnnual">Bi-Annual</option>
              <option value="annual">Annual</option>
            </select>
            
            {(statusFilter !== 'all' || searchTerm) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setStatusFilter('all');
                  setSearchTerm('');
                }}
                className="ml-2"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Donation Types Table */}
      <div className="card">
        {filteredTypes.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {donationTypes.length === 0 ? 'No donation types found' : 'No types match your filters'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {donationTypes.length === 0 
                ? 'Get started by adding your first donation type.'
                : searchTerm 
                  ? 'Try adjusting your search criteria or status filter.'
                  : 'Try adjusting your status filter to see more types.'
              }
            </p>
          </div>
        ) : (
          <TableRoot>
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Status</TH>
                  <TH>Donation Amount ({getPeriodLabel(timePeriod)})</TH>
                  <TH>Created At</TH>
                  <TH>Actions</TH>
                </TR>
              </THead>
              <TBody>
                {filteredTypes.map((type) => (
                  <TR key={type._id} className={!type.isActive ? 'bg-gray-50 dark:bg-gray-800/50' : ''}>
                    <TD className="font-medium">{type.name}</TD>
                    <TD>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        type.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {type.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </TD>
                    <TD className="font-medium">
                      <div>
                        <div className="text-green-600 dark:text-green-400">
                          {formatAmount(type.periodAmount)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {type.periodCount} donation{type.periodCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </TD>
                    <TD className="text-sm text-gray-500">{formatDate(type.createdAt)}</TD>
                    <TD>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(type)}
                          leftIcon={<Edit className="h-4 w-4" />}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(type)}
                          className={type.isActive ? 'text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300' : 'text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300'}
                        >
                          {type.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(type)}
                          leftIcon={<Trash2 className="h-4 w-4" />}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        >
                          Delete
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableRoot>
        )}
        
        {/* Summary */}
        {filteredTypes.length > 0 && (
          <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredTypes.length} of {donationTypes.length} donation types
            {statusFilter !== 'all' && ` (${statusFilter} only)`}
            {searchTerm && ` matching "${searchTerm}"`}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {editingType ? 'Edit Donation Type' : 'Add Donation Type'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">Name *</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, name: e.target.value }));
                    if (formErrors.name) setFormErrors(prev => ({ ...prev, name: '' }));
                  }}
                  placeholder="e.g., Zakat, Sadaqah, Fitrana"
                  className={formErrors.name ? 'border-red-500' : ''}
                  required
                />
                {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
              </div>

              <div>
                <label className="form-label">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description of this donation type"
                  className="form-textarea w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 resize-none"
                  rows={3}
                />
              </div>

              <div className="flex space-x-2 pt-4">
                <Button type="submit" variant="primary" className="flex-1">
                  {editingType ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="secondary" onClick={closeModal} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg mr-3">
                <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Confirm Deletion
              </h3>
            </div>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete <strong>"{deletingType.name}"</strong>? 
              This action cannot be undone and will permanently remove the donation type.
            </p>

            <div className="flex space-x-2">
              <Button
                variant="error"
                onClick={handleDeleteConfirm}
                className="flex-1"
              >
                Delete
              </Button>
              <Button
                variant="secondary"
                onClick={closeDeleteConfirm}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DonationTypes;
